import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingBag, Loader2 } from 'lucide-react';
import { getProducts } from '@/lib/api/endpoints';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(rupees);
};

const ProductCard = ({ p, onAdd }) => {
  const img = p.images?.[0]?.thumbnail || p.images?.[0]?.src;
  return (
    <article className="group bg-[#efe6d3] lift">
      <Link
        to={`/product/${p.id}`}
        className="block relative aspect-square rounded-full halo overflow-hidden"
      >
        {img ? (
          <img
            src={img}
            alt={p.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-3xl text-[#b78d3c]">
            R
          </div>
        )}
      </Link>
      <div className="p-5">
        <h3 className="font-display text-xl mb-1">{p.name}</h3>
        <p className="text-xs text-[#1a120b]/60 italic line-clamp-1 mb-3">
          {p.short_description?.replace(/<[^>]+>/g, '').slice(0, 80) || '—'}
        </p>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">{formatPrice(p.prices?.price, p.prices?.currency_code)}</span>
            {p.prices?.regular_price &&
              p.prices.regular_price !== p.prices.price && (
                <span className="text-[#1a120b]/45 line-through ml-1.5 text-xs">
                  {formatPrice(p.prices.regular_price, p.prices?.currency_code)}
                </span>
              )}
          </div>
          <button
            onClick={() => onAdd(p.id, p.name)}
            className="w-9 h-9 border border-[#1a120b] hover:bg-[#1a120b] hover:text-[#f6efe1] flex items-center justify-center transition-colors"
            aria-label={`Add ${p.name} to cart`}
          >
            <ShoppingBag className="w-4 h-4" />
          </button>
        </div>
      </div>
    </article>
  );
};

const Shop = () => {
  const { addItem } = useCart();
  const [filter, setFilter] = useState('all');

  const { data: allProducts = [], isLoading, error } = useQuery({
    queryKey: ['products', { per_page: 100 }],
    queryFn: () => getProducts({ per_page: 100 }),
  });

  // Build Your Own Box products (build-box-8/12/24) exist so the box
  // builder can add them to cart by SKU — they're set to Catalog
  // Visibility: Hidden in wp-admin, but the Store API's products list
  // doesn't filter by that, so they'd otherwise show up here as normal
  // shop items. Filtered out client-side until/unless WP-side filtering
  // is sorted out.
  const products = allProducts.filter((p) => !/^build-box-\d+$/.test(p.sku || ''));

  const handleAdd = async (productId, name) => {
    try {
      await addItem({ id: productId, quantity: 1 });
      toast.success(`Added to your box`);
    } catch {
      /* queryClient mutation onError already toasts */
    }
  };

  return (
    <main>
      <section className="bg-[#1a120b] text-[#f6efe1] py-24">
        <div className="container-lux text-center">
          <p className="tracking-luxe text-[#b78d3c] mb-4">The Atelier</p>
          <h1 className="font-display text-6xl md:text-7xl mb-4">
            Sweetness overloaded.
          </h1>
          <p className="text-[#f6efe1]/70 max-w-xl mx-auto">
            Curated selections, each one a delightful signature.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-lux">
          {isLoading ? (
            <div className="flex justify-center py-24 text-[#1a120b]/50">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-24 text-red-600">
              <p>Could not load products. Please try again.</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 text-[#1a120b]/50">
              <p>No products available right now.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} onAdd={handleAdd} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="container-lux max-w-2xl text-center">
          <p className="tracking-luxe text-[#b78d3c] mb-3">Or…</p>
          <h2 className="font-display text-4xl md:text-5xl mb-6">
            Compose your own selection.
          </h2>
          <Link to="/build-a-box" className="btn-primary">
            Curate a Box
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Shop;
